import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export const PENDING_PLATFORM_UPGRADE_KEY = "pending_platform_upgrade";
export const BILLING_SETUP_PATH = "/app/settings/billing";

export function redirectToBillingSetupWizard() {
  window.location.assign(`${BILLING_SETUP_PATH}?setup=1`);
}

/**
 * Handles free → paid checkout return: reconciles Stripe with the studio,
 * then sends the owner to the billing setup wizard.
 */
export function usePostCheckoutUpgrade() {
  const { getAuthToken, isSubAccount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const runningRef = useRef(false);

  useEffect(() => {
    if (isSubAccount) {
      return;
    }

    async function handlePostCheckout() {
      if (runningRef.current) {
        return;
      }

      const authToken = getAuthToken();
      if (!authToken) {
        return;
      }

      const params = new URLSearchParams(location.search);
      const sessionId = params.get("session_id");
      const onUpgradeCompletePage =
        location.pathname === "/app/upgrade-complete";
      const pendingUpgrade =
        sessionStorage.getItem(PENDING_PLATFORM_UPGRADE_KEY) === "1";

      if (!sessionId && !onUpgradeCompletePage && !pendingUpgrade) {
        return;
      }

      runningRef.current = true;

      try {
        const reconcileResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/reconcile-free-upgrade`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(sessionId ? { sessionId } : {}),
          }
        );

        if (reconcileResponse.ok) {
          const reconcileData = await reconcileResponse.json();
          if (reconcileData.upgraded) {
            sessionStorage.removeItem(PENDING_PLATFORM_UPGRADE_KEY);
            redirectToBillingSetupWizard();
            return;
          }
        }

        const statusResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/billing-setup/status`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (statusResponse.ok) {
          const status = await statusResponse.json();
          if (status.showWizard) {
            sessionStorage.removeItem(PENDING_PLATFORM_UPGRADE_KEY);
            if (!location.pathname.startsWith(BILLING_SETUP_PATH)) {
              navigate(`${BILLING_SETUP_PATH}?setup=1`, { replace: true });
            }
            return;
          }
        }

        if (onUpgradeCompletePage) {
          navigate(BILLING_SETUP_PATH, { replace: true });
        }
      } catch (error) {
        console.error("Post-checkout upgrade handling failed:", error);
        runningRef.current = false;
      }
    }

    handlePostCheckout();
  }, [
    getAuthToken,
    isSubAccount,
    location.pathname,
    location.search,
    navigate,
  ]);
}

export function useBillingSetupRedirect() {
  const { getAuthToken, isSubAccount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const checkedRef = useRef(false);

  useEffect(() => {
    if (isSubAccount) {
      return;
    }

    if (location.pathname.startsWith(BILLING_SETUP_PATH)) {
      return;
    }

    if (location.pathname === "/app/upgrade-complete") {
      return;
    }

    if (checkedRef.current) {
      return;
    }

    checkedRef.current = true;

    async function redirectIfSetupNeeded() {
      try {
        const authToken = getAuthToken();
        if (!authToken) {
          checkedRef.current = false;
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/billing-setup/status`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          checkedRef.current = false;
          return;
        }

        const status = await response.json();
        if (status.showWizard) {
          navigate(`${BILLING_SETUP_PATH}?setup=1`, { replace: true });
        }
      } catch {
        checkedRef.current = false;
      }
    }

    redirectIfSetupNeeded();
  }, [getAuthToken, isSubAccount, location.pathname, navigate]);
}
